// frontend/src/components/planner/DebtPayoffPanel.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingDown,
  DollarSign,
  Calendar,
  Award,
  AlertTriangle,
  ChevronRight,
  Plus,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import api from '../../utils/api';
import AppShell from '../layout/AppShell';
import LoadingSpinner from '../common/LoadingSpinner';

export default function DebtPayoffPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState(null);
  const [hasDebts, setHasDebts] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);

  useEffect(() => {
    loadComparison();
  }, []);

  // Fetch AI insight when debts are loaded
  useEffect(() => {
    if (hasDebts && comparison) {
      setInsightLoading(true);
      api.get('/api/planner/debt-insight')
        .then(res => setInsight(res.data.insight))
        .catch(() => setInsight(null))
        .finally(() => setInsightLoading(false));
    }
  }, [hasDebts, comparison]);

  async function loadComparison() {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/api/planner/debt-comparison');
      
      if (response.data.success) {
        setHasDebts(response.data.hasDebts);
        setComparison(response.data);
      } else {
        setError(response.data.error || 'Failed to load debt comparison');
      }
    } catch (err) {
      console.error('Error loading comparison:', err);
      setError('Failed to load debt comparison');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadComparison();
    setRefreshing(false);
  }

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatMonths = (months) => {
    if (!months) return '—';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years === 0) return `${remainingMonths} months`;
    if (remainingMonths === 0) return `${years} year${years > 1 ? 's' : ''}`;
    return `${years}y ${remainingMonths}m`;
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" text="Analyzing your debts..." />
        </div>
      </AppShell>
    );
  }

  if (!hasDebts) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto mt-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Debts Tracked</h2>
            <p className="text-base sm:text-sm text-gray-600 mb-6">
              Add your debts to see personalized payoff strategies and compare avalanche vs. snowball methods.
            </p>
            <button
              onClick={() => navigate('/planner/recurring')}
              className="inline-flex items-center gap-2 px-6 py-3 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add a Debt
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const { avalanche, snowball, savings, timeDifference, savingsDescription } = comparison?.comparison || {};
  const isAvalancheBetter = savings > 0;

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Debt Payoff Strategy</h1>
            <p className="text-base sm:text-sm text-gray-600 mt-1">Compare avalanche vs. snowball methods</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-base sm:text-sm text-gray-600">Total Debt</p>
                <p className="text-base sm:text-xl font-bold text-red-600">
                  {formatCurrency(comparison?.totalDebt)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-base sm:text-sm text-gray-600">Extra Monthly Payment</p>
                <p className="text-base sm:text-xl font-bold text-blue-600">
                  {formatCurrency(comparison?.extraPayment)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-base sm:text-sm text-gray-600">Debts Tracked</p>
                <p className="text-base sm:text-xl font-bold text-amber-600">
                  {comparison?.debtCount || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Strategy Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Avalanche Strategy */}
          <div className={`bg-white rounded-lg shadow-sm border-2 ${isAvalancheBetter ? 'border-emerald-500' : 'border-gray-200'} overflow-hidden`}>
            {isAvalancheBetter && (
              <div className="bg-emerald-500 text-white text-center py-2 text-sm font-semibold flex items-center justify-center gap-2">
                <Award className="w-4 h-4" />
                Recommended - Saves the Most
              </div>
            )}
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Avalanche Method</h3>
              <p className="text-base sm:text-sm text-gray-600 mb-4">
                Pay highest interest rate first. Mathematically optimal.
              </p>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Time to Debt-Free</span>
                  <span className="font-semibold text-gray-900">{formatMonths(avalanche?.months)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Interest Paid</span>
                  <span className="font-semibold text-red-600">{formatCurrency(avalanche?.totalInterest)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Snowball Strategy */}
          <div className={`bg-white rounded-lg shadow-sm border-2 ${!isAvalancheBetter ? 'border-emerald-500' : 'border-gray-200'} overflow-hidden`}>
            {!isAvalancheBetter && (
              <div className="bg-emerald-500 text-white text-center py-2 text-sm font-semibold flex items-center justify-center gap-2">
                <Award className="w-4 h-4" />
                Recommended
              </div>
            )}
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Snowball Method</h3>
              <p className="text-base sm:text-sm text-gray-600 mb-4">
                Pay smallest balance first. Quick wins for motivation.
              </p>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Time to Debt-Free</span>
                  <span className="font-semibold text-gray-900">{formatMonths(snowball?.months)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Interest Paid</span>
                  <span className="font-semibold text-red-600">{formatCurrency(snowball?.totalInterest)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Savings Highlight */}
        {Math.abs(savings) > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-emerald-900 text-lg">
                  {savingsDescription}
                </h4>
                <p className="text-emerald-800 mt-1">
                  {isAvalancheBetter 
                    ? `Using the avalanche method, you'll pay off your debt ${Math.abs(timeDifference)} month${Math.abs(timeDifference) !== 1 ? 's' : ''} ${timeDifference < 0 ? 'faster' : 'slower'} while saving ${formatCurrency(Math.abs(savings))} in interest.`
                    : 'The snowball method provides quicker wins but may cost slightly more in interest.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Strategy Explanation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Understanding the Methods</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">🔺 Avalanche Method</h4>
              <ul className="space-y-2 text-base sm:text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Pay minimum on all debts
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Put extra money toward highest interest rate
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Mathematically saves the most money
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  May take longer to see first debt paid off
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">❄️ Snowball Method</h4>
              <ul className="space-y-2 text-base sm:text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Pay minimum on all debts
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Put extra money toward smallest balance
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Quick wins build momentum and motivation
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  May pay more interest overall
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* AI Insight Card */}
        {insight && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="text-xl flex-shrink-0">&#x1F4A1;</span>
              <div>
                <p className="text-sm font-medium text-amber-900">AI Insight</p>
                <p className="text-base sm:text-sm text-amber-800 mt-1">{insight}</p>
              </div>
            </div>
          </div>
        )}
        {insightLoading && (
          <div className="p-4 bg-gray-50 rounded-xl animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/planner/recurring')}
            className="flex items-center justify-between p-4 min-h-[44px] bg-white rounded-lg shadow-sm border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3">
              <Plus className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-gray-900">Add More Debts</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" />
          </button>
          
          <button
            onClick={() => navigate('/planner/settings')}
            className="flex items-center justify-between p-4 min-h-[44px] bg-white rounded-lg shadow-sm border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-gray-900">Set Extra Payment Amount</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" />
          </button>
        </div>
      </div>
    </AppShell>
  );
}
